import { Species, SpeciesSurveyDataV1, WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { SpeciesCard } from '../../components/map/map_modals/lakes_modal/tab_species/species_card';
import { speciesSurveyDataKeysSort } from '../../components/map/map_modals/lakes_modal/tab_species/species_list';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import { WaterbodyDetailHeader } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import { WaterbodySeoPagesNav } from '../../components/waterbody_seo_pages_nav/waterbody_seo_pages_nav';
import { useReferenceData } from '../../hooks/use_reference_data';
import { formatToIsoDateZ } from '../../lib/time';
import { RoutePaths, WaterbodyPaths } from '../../routes';

interface WaterbodySpeciesListPageProps {
  mostRecentFishingReportDate: dayjs.Dayjs | null;
  waterbodyDetail: WaterbodyDetail;
  waterbodySpeciesSurveyData: SpeciesSurveyDataV1;
}

export const WaterbodySpeciesListPage = (props: WaterbodySpeciesListPageProps) => {
  const { mostRecentFishingReportDate, waterbodyDetail, waterbodySpeciesSurveyData } = props;
  const { url_slug, has_survey_chart_data, primary_name } = waterbodyDetail;

  const history = useHistory();
  const { allSpecies } = useReferenceData();

  const lastUpdatedISODate = formatToIsoDateZ(mostRecentFishingReportDate);

  const speciesKeysSorted = useMemo(
    () => speciesSurveyDataKeysSort(waterbodySpeciesSurveyData),
    [waterbodySpeciesSurveyData]
  );

  const onSpeciesSelect = (species: Species) => {
    history.push(`${RoutePaths.WATERS}/${url_slug}${WaterbodyPaths.SPECIES}/${species.name}`);
  };

  const [loaded, setLoaded] = React.useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section className="bg-[#e9eeec]">
      <SEO
        title={`Fish Species in ${primary_name} • Size & Abundance Trends`}
        description={`See which fish thrive in ${primary_name}, average sizes, historical CPUE and gear that catches them.`}
      />
      <ScrollToTop>
        <WaterbodyDetailHeader
          headingTag="p"
          lastUpdatedDate={lastUpdatedISODate}
          productToShowInFollowModal={null}
          scrollToFishingReport={() => null}
          showPublishDates
          waterbody={waterbodyDetail}
          navElement={null}
          showBreadcrumbs={false}
          showButtons={false}
        />
        <div className="py-6">
          <ContentWrapper>
            <div className="mb-6">
              <WaterbodySeoPagesNav
                urlSlug={url_slug}
                has_survey_chart_data={has_survey_chart_data}
              />
            </div>
            <div className="p-6 bg-white rounded-lg">
              <h1 className="mb-1 text-2xl">Fish Species in {primary_name}</h1>
              <h2 className="mb-6 text-base font-normal">
                Population trends, size ranges & seasonal patterns.
              </h2>

              <div className="flex flex-col gap-3">
                {loaded &&
                  speciesKeysSorted.map((key) => {
                    const speciesData = waterbodySpeciesSurveyData.species[key];
                    const species = allSpecies.find((s) => s.display_name === key);
                    if (!species || !speciesData) {
                      return null;
                    }

                    return (
                      <div key={key} className="border rounded-lg">
                        <SpeciesCard
                          species={species}
                          speciesData={speciesData}
                          onClick={onSpeciesSelect}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </ContentWrapper>
        </div>
      </ScrollToTop>
    </section>
  );
};
